import type { Team } from '@compass/client/types/team';
import { teamService } from '@services/api';
import { queryKeys } from '@services/query.config';
import { useStore } from '@store/index';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

/**
 * Returns the teams in which the current user is the manager, scoped to the
 * currently-selected organization.
 *
 * Source: `GET /api/v1/teams/my` (returns teams where the user is member OR
 * manager across every org the user belongs to). We then narrow to
 * `manager_id === userId` AND `organization_id === selectedOrganizationId`,
 * because the Workforce Console is org-scoped (the URL itself is
 * `/org-{id}/workforce`).
 *
 * `isManagerOfAny` reflects manager status WITHIN the currently-selected
 * org — switching to an org where the user manages nothing hides the
 * Workforce Console sidebar entry until they switch back.
 *
 * This is the Workforce Console's access rule while the
 * `permissions_based_wc_access` flag is off (BV-2303). `enabled: false` sends
 * no request, for when the flag is on.
 */
export const useMyManagedTeams = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const userId = useStore(state => state.user?.id ?? '');
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);

  const query = useQuery({
    queryKey: queryKeys.teams.current(userId),
    queryFn: () => teamService.getCurrentUserTeams({}),
    enabled: enabled && !!userId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const allTeams: Team[] = useMemo(() => query.data?.teams ?? [], [query.data]);

  const managedTeams = useMemo(
    () =>
      userId && selectedOrganizationId
        ? allTeams.filter(
            t => t.manager_id === userId && t.organization_id === selectedOrganizationId,
          )
        : [],
    [allTeams, userId, selectedOrganizationId],
  );

  return {
    managedTeams,
    /** True when the user manages at least one team in the currently-selected org. */
    isManagerOfAny: managedTeams.length > 0,
    isLoading: query.isLoading,
    error: query.error,
  };
};
